/**
 * 호 상세 페이지 하단 자료 탭 — 통칙 / 부주 / 류주 / 호해설 / 분류사례 / 이미지.
 *
 * Props:
 *  - hs4 / chapter / section: 자료 fetch param
 *  - lang: 'kor' | 'eng'
 *
 * 각 탭이 독립적 fetch — 활성화될 때만 데이터 load.
 * 호해설 default active.
 *
 * 부주/류주/호해설은 모두 /api/onto/notes-search 사용 + label 의 한글 키워드
 * ('부주', '류주', '호해설') 로 client-side 필터.
 */
import React, { useState, useEffect, useCallback } from "react";
import { ChevronDown, ChevronUp, Search } from "lucide-react";
import { PrecedentRow } from "./EvidencePanel";
import PrecedentsPage from "./PrecedentsPage";
import { CLIP_IMG_BASE } from "./precedentAssets";
import StarHistoryPanel from "./StarHistoryPanel";

export { CLIP_IMG_BASE } from "./precedentAssets";

export default function HeadingTabs({
  hs4,
  chapter,
  section,
  lang,
  hsk,
  tabBarRef = null,
  stickyTop = 0,
  maximized = false,
  onToggleMaximized = null,
  onResizeStart = null,
}) {
  const [active, setActive] = useState(() => {
    if (typeof window === "undefined") return "heading";
    return new URLSearchParams(window.location.search).get("tab") === "images"
      ? "images"
      : "heading";
  });
  // 호/세번에서 여는 일반 분류사례와 이미지에서 특정 사례를 고른 진입을
  // 하나의 최신 좌목록·우상세 화면으로 통합한다.
  const [precedentSelection, setPrecedentSelection] = useState({
    hsk: hsk || hs4 || null,
    caseId: null,
  });
  const [evidenceSearchInput, setEvidenceSearchInput] = useState("");
  const [evidenceSearchQuery, setEvidenceSearchQuery] = useState("");
  const [evidenceCounts, setEvidenceCounts] = useState({});

  useEffect(() => {
    setPrecedentSelection({ hsk: hsk || hs4 || null, caseId: null });
    setEvidenceSearchInput("");
    setEvidenceSearchQuery("");
    setEvidenceCounts({});
  }, [hs4, hsk]);

  const updateEvidenceCount = useCallback((tabId, meta) => {
    setEvidenceCounts((previous) => {
      const before = previous[tabId] || {};
      const next = {
        ...before,
        shown: Number(meta?.shown || 0),
        limited: !!meta?.limited,
      };
      if (!evidenceSearchQuery) next.all = Number(meta?.shown || 0);
      return { ...previous, [tabId]: next };
    });
  }, [evidenceSearchQuery]);
  const handlePrecedentResults = useCallback(
    (meta) => updateEvidenceCount("precedents", meta),
    [updateEvidenceCount],
  );
  const handleImageResults = useCallback(
    (meta) => updateEvidenceCount("images", meta),
    [updateEvidenceCount],
  );

  const applyEvidenceSearch = () => {
    setEvidenceSearchQuery(evidenceSearchInput.trim());
  };

  const clearEvidenceSearch = () => {
    setEvidenceSearchInput("");
    setEvidenceSearchQuery("");
  };

  const selectTab = (tabId) => {
    if (tabId === "precedents") {
      // 이미지에서 특정 사례를 열었더라도 탭 자체를 다시 누르면 현재
      // 호/세번의 최신 분류사례 화면 첫 항목부터 보여 준다.
      setPrecedentSelection({ hsk: hsk || hs4 || null, caseId: null });
    }
    setActive(tabId);
  };

  const openImageCase = (code, caseId) => {
    const digits = String(code || "").replace(/\D/g, "");
    if (digits.length < 4) return;
    setPrecedentSelection({
      hsk: digits.length >= 10 ? digits.slice(0, 10) : null,
      caseId: String(caseId || "").trim() || null,
    });
    setActive("precedents");
  };

  // 세번(hsk) 단계면 분류사례 라벨에 "(세번)" 표기 — hsk 정확 매칭.
  const TABS = [
    { id: "general-rules", label: "통칙" },
    { id: "section", label: "부주" },
    { id: "chapter", label: "류주" },
    { id: "heading", label: "호해설" },
    { id: "precedents", label: hsk ? "분류사례 (세번)" : "분류사례" },
    { id: "foreign", label: "해외사례" },
    { id: "images", label: "이미지" },
    { id: "agency", label: "관세사분류사례" },
  ];
  return (
    <div
      data-testid="heading-tabs-shell"
      data-maximized={maximized ? "true" : "false"}
      style={{
        position: "relative",
        marginTop: onToggleMaximized ? 18 : 10,
        minHeight: maximized ? "calc(100% - 18px)" : undefined,
        border: "1px solid var(--audit-border)",
        borderRadius: 6,
        background: "var(--audit-bg-elevated)",
      }}
    >
      {onToggleMaximized && (
        <div
          data-testid="heading-tabs-resize-line"
          role="separator"
          aria-label="관세율표와 자료 탭 높이 조절"
          aria-orientation="horizontal"
          onMouseDown={maximized ? undefined : onResizeStart}
          className="audit-print-hide"
          style={{
            ...tabResizeLineStyle,
            cursor: maximized ? "default" : "ns-resize",
          }}
        >
          <span aria-hidden="true" style={tabResizeRuleStyle} />
          <button
            type="button"
            onMouseDown={(event) => {
              // 중앙 버튼은 이동선 드래그와 분리한다.
              event.preventDefault();
              event.stopPropagation();
            }}
            onClick={onToggleMaximized}
            aria-label={maximized ? "자료 탭 원래 크기로" : "자료 탭 최대화"}
            title={
              maximized
                ? "관세율표와 함께 보는 원래 위치로 돌아가기"
                : "자료 탭을 상단까지 크게 보기"
            }
            style={tabMaximizeButtonStyle}
          >
            {maximized ? <ChevronDown size={13} /> : <ChevronUp size={13} />}
          </button>
        </div>
      )}
      <div
        ref={tabBarRef}
        data-testid="heading-tabs-bar"
        style={{
          display: "flex",
          borderBottom: "1px solid var(--audit-border)",
          background: "var(--audit-bg-subtle)",
          position: "sticky",
          top: maximized ? 0 : stickyTop,
          zIndex: 8,
          overflowX: "auto",
        }}
      >
        {TABS.map((t) => {
          const isActive = active === t.id;
          return (
            <button
              key={t.id}
              onClick={() => selectTab(t.id)}
              style={{
                padding: "7px 14px",
                border: "none",
                borderBottom: isActive
                  ? "2px solid var(--audit-accent)"
                  : "2px solid transparent",
                background: "transparent",
                color: isActive
                  ? "var(--audit-accent)"
                  : "var(--audit-text)",
                cursor: "pointer",
                fontFamily: "inherit",
                fontSize: 12,
                fontWeight: isActive ? 700 : 500,
              }}
            >
              {t.label}
            </button>
          );
        })}
      </div>
      {(active === "precedents" || active === "images") && (
        <ScopedEvidenceSearch
          scopeCode={active === "precedents"
            ? (precedentSelection.hsk || hsk || hs4)
            : (hsk || hs4)}
          activeTab={active}
          input={evidenceSearchInput}
          query={evidenceSearchQuery}
          count={evidenceCounts[active]}
          onInputChange={(value) => {
            setEvidenceSearchInput(value);
            if (!value) setEvidenceSearchQuery("");
          }}
          onSearch={applyEvidenceSearch}
          onClear={clearEvidenceSearch}
        />
      )}
      <div style={{ padding: active === "precedents" || active === "foreign" ? 0 : 14 }}>
        {active === "general-rules" && <GeneralRulesPanel />}
        {active === "section" && (
          <NotesPanel
            hs4={hs4}
            chapter={chapter}
            section={section}
            lang={lang}
            noteLabel="부주"
            empty="부 제목·부주 원문 없음"
          />
        )}
        {active === "chapter" && (
          <NotesPanel
            hs4={hs4}
            chapter={chapter}
            section={section}
            lang={lang}
            noteLabel="류주"
            empty="류주 본문 없음"
          />
        )}
        {active === "heading" && (
          <NotesPanel
            hs4={hs4}
            chapter={chapter}
            section={section}
            lang={lang}
            noteLabel="호해설"
            empty="호해설 본문 없음"
          />
        )}
        {active === "precedents" &&
          (precedentSelection.hsk && /^(?:[0-9]{4}|[0-9]{10})$/.test(precedentSelection.hsk) ? (
            <PrecedentsPage
              key={`${precedentSelection.hsk}:${precedentSelection.caseId || ""}:${evidenceSearchQuery}`}
              inlineHsk={precedentSelection.hsk}
              inlineCaseId={precedentSelection.caseId}
              inlineKeyword={evidenceSearchQuery}
              onInlineResultsChange={handlePrecedentResults}
            />
          ) : (
            <PrecedentsPanel hs4={hs4} hsk={hsk} />
          ))}
        {/* 해외사례 — 오른쪽 근거 자료에 있던 블록을 하단 탭으로 옮겼다(문의 #74). */}
        {active === "foreign" && (
          <PrecedentsPage
            key={`foreign:${hsk || hs4}`}
            inlineHsk={hsk || hs4}
            inlineSource="foreign"
          />
        )}
        {active === "images" && (
          <ImagesPanel
            hs4={hs4}
            hsk={hsk}
            query={evidenceSearchQuery}
            onResultsChange={handleImageResults}
            onOpenCases={openImageCase}
          />
        )}
        {active === "agency" && <><StarHistoryPanel hs4={hs4} /><AgencyPrecedentsPanel hs4={hs4} /></>}
      </div>
    </div>
  );
}

function ScopedEvidenceSearch({
  scopeCode,
  activeTab,
  input,
  query,
  count,
  onInputChange,
  onSearch,
  onClear,
}) {
  const scope = formatEvidenceScope(scopeCode);
  const shown = Number(count?.shown || 0);
  const all = Number.isFinite(count?.all) ? Number(count.all) : null;
  const countText = query && all != null
    ? `${shown.toLocaleString()}/${all.toLocaleString()}건`
    : `${shown.toLocaleString()}건`;

  return (
    <form
      data-testid="scoped-evidence-search"
      onSubmit={(event) => {
        event.preventDefault();
        onSearch();
      }}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        padding: "7px 12px",
        borderBottom: "1px solid var(--audit-border)",
        background: "var(--audit-bg)",
      }}
    >
      <div style={{ position: "relative", flex: 1, minWidth: 0 }}>
        <Search
          size={13}
          aria-hidden="true"
          style={{
            position: "absolute",
            left: 9,
            top: "50%",
            transform: "translateY(-50%)",
            color: "var(--audit-text-muted)",
            pointerEvents: "none",
          }}
        />
        <input
          type="search"
          aria-label={`현재 ${scope} 내 ${activeTab === "images" ? "이미지 사례" : "분류사례"} 검색`}
          value={input}
          onChange={(event) => onInputChange(event.target.value)}
          placeholder={`현재 ${scope} 사례에서 품명·물품설명·결정이유 검색`}
          style={{
            width: "100%",
            boxSizing: "border-box",
            height: 30,
            padding: "4px 30px 4px 28px",
            border: "1px solid var(--audit-border-strong)",
            borderRadius: 5,
            background: "var(--audit-bg)",
            color: "var(--audit-text)",
            fontFamily: "inherit",
            fontSize: 13,
          }}
        />
        {input && (
          <button
            type="button"
            aria-label="현재 세번 내 검색어 지우기"
            title="검색어 지우기"
            onClick={onClear}
            style={{
              position: "absolute",
              right: 5,
              top: "50%",
              transform: "translateY(-50%)",
              width: 22,
              height: 22,
              padding: 0,
              border: 0,
              background: "transparent",
              color: "var(--audit-text-muted)",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: 16,
              lineHeight: "22px",
            }}
          >
            ×
          </button>
        )}
      </div>
      <button
        type="submit"
        className="audit-btn"
        disabled={!input.trim() || input.trim() === query}
        style={{ height: 30, whiteSpace: "nowrap" }}
      >
        검색
      </button>
      <span
        aria-live="polite"
        style={{
          minWidth: 62,
          textAlign: "right",
          color: "var(--audit-text-muted)",
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 12,
          whiteSpace: "nowrap",
        }}
      >
        {countText}{count?.limited ? "+" : ""}
      </span>
    </form>
  );
}

function formatEvidenceScope(code) {
  const digits = String(code || "").replace(/\D/g, "");
  if (digits.length >= 10) {
    return `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6, 10)}`;
  }
  if (digits.length >= 4) return `제${digits.slice(0, 4)}호`;
  return "현재 호·세번";
}

const tabResizeLineStyle = {
  position: "absolute",
  top: -18,
  left: -1,
  right: -1,
  height: 18,
  userSelect: "none",
  zIndex: 10,
};

const tabResizeRuleStyle = {
  position: "absolute",
  top: 8,
  left: 0,
  right: 0,
  borderTop: "1px solid var(--audit-border-strong, #c5c9cf)",
  pointerEvents: "none",
};

const tabMaximizeButtonStyle = {
  position: "absolute",
  top: 0,
  left: "50%",
  width: 42,
  height: 17,
  padding: 0,
  transform: "translateX(-50%)",
  display: "inline-flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1px solid #374151",
  borderRadius: 5,
  background: "#374151",
  color: "#ffffff",
  cursor: "pointer",
  boxShadow: "none",
  zIndex: 1,
};

function GeneralRulesPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState("1");

  useEffect(() => {
    setLoading(true);
    fetch("/api/onto/general-rules")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))   // 네트워크 실패는 "자료 없음"으로(처리되지 않은 오류를 남기지 않는다)
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        불러오는 중…
      </div>
    );
  }
  if (!data || !Array.isArray(data.rules) || data.rules.length === 0) {
    return <div style={emptyStyle}>통칙 원문을 불러오지 못했습니다.</div>;
  }

  const visible = selected === "all"
    ? data.rules
    : data.rules.filter((rule) => String(rule.id) === selected);

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: 5,
          marginBottom: 12,
        }}
      >
        <button
          type="button"
          onClick={() => setSelected("all")}
          aria-pressed={selected === "all"}
          style={ruleButtonStyle(selected === "all")}
        >
          전체
        </button>
        {data.rules.map((rule) => (
          <button
            key={rule.id}
            type="button"
            onClick={() => setSelected(String(rule.id))}
            aria-pressed={selected === String(rule.id)}
            style={ruleButtonStyle(selected === String(rule.id))}
          >
            통{rule.id}
          </button>
        ))}
      </div>

      <div style={{ marginBottom: 12 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: "var(--audit-text)" }}>
          {data.title}
        </div>
        <div style={{ marginTop: 4, fontSize: 12, color: "var(--audit-text-muted)" }}>
          {data.intro}
        </div>
      </div>

      {visible.map((rule, index) => (
        <section
          key={rule.id}
          aria-labelledby={`general-rule-${rule.id}`}
          style={{
            padding: "10px 0 12px",
            borderTop: index > 0 ? "1px solid var(--audit-border)" : "none",
          }}
        >
          <div
            id={`general-rule-${rule.id}`}
            style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 7 }}
          >
            <strong style={{ fontSize: 13.5 }}>{rule.label}</strong>
            {rule.scope_label && (
              <span
                style={{
                  padding: "1px 6px",
                  borderRadius: 10,
                  background: "var(--audit-bg-subtle)",
                  border: "1px solid var(--audit-border)",
                  color: "var(--audit-text-muted)",
                  fontSize: 11.5,
                }}
              >
                {rule.scope_label}
              </span>
            )}
          </div>
          {(rule.paragraphs || []).map((paragraph, paragraphIndex) => (
            <p
              key={paragraphIndex}
              style={{
                margin: paragraphIndex === 0 ? 0 : "7px 0 0",
                fontSize: 13,
                lineHeight: "21px",
                color: "var(--audit-text)",
                whiteSpace: "pre-wrap",
              }}
            >
              <RichRuleText text={paragraph} />
            </p>
          ))}
          {(rule.explanation_sections || []).map((section, sectionIndex) => (
            <div
              key={`${rule.id}-explanation-${sectionIndex}`}
              style={{
                marginTop: 12,
                padding: "10px 12px",
                border: "1px solid var(--audit-border)",
                borderRadius: 6,
                background: "var(--audit-bg-subtle)",
              }}
            >
              <div
                style={{
                  marginBottom: 7,
                  fontSize: 12.5,
                  lineHeight: "19px",
                  fontWeight: 700,
                  color: "var(--audit-text)",
                }}
              >
                {section.title}
              </div>
              {(section.paragraphs || []).map((paragraph, paragraphIndex) => (
                <p
                  key={paragraphIndex}
                  style={{
                    margin: paragraphIndex === 0 ? 0 : "8px 0 0",
                    fontSize: 12.5,
                    lineHeight: "20px",
                    color: "var(--audit-text)",
                    whiteSpace: "pre-wrap",
                  }}
                >
                  <RichRuleText text={paragraph} />
                </p>
              ))}
            </div>
          ))}
          {(rule.footnotes || []).map((footnote, footnoteIndex) => (
            <div
              key={`${rule.id}-footnote-${footnoteIndex}`}
              style={{
                marginTop: 8,
                fontSize: 12,
                lineHeight: "18px",
                color: "var(--audit-text-muted)",
              }}
            >
              {footnote}
            </div>
          ))}
          {rule.application_note && (
            <div
              style={{
                marginTop: 8,
                padding: "7px 9px",
                borderLeft: "3px solid var(--audit-border-strong, #9ca3af)",
                background: "var(--audit-bg-subtle)",
                color: "var(--audit-text-muted)",
                fontSize: 12,
                lineHeight: "18px",
              }}
            >
              적용 메모: {rule.application_note}
            </div>
          )}
        </section>
      ))}

      {data.source?.url && (
        <div
          style={{
            marginTop: 10,
            paddingTop: 10,
            borderTop: "1px solid var(--audit-border)",
            fontSize: 12,
            color: "var(--audit-text-muted)",
          }}
        >
          출처: {data.source.publisher} · {data.source.document}{" "}
          <a href={data.source.url} target="_blank" rel="noreferrer">
            공식 원문 ↗
          </a>
        </div>
      )}
    </div>
  );
}

function RichRuleText({ text }) {
  const value = String(text || "");
  const parts = [];
  const markdownLink = /\[([^\]]+)]\((https?:\/\/[^)]+)\)/g;
  let cursor = 0;
  let match;
  while ((match = markdownLink.exec(value)) !== null) {
    if (match.index > cursor) parts.push(value.slice(cursor, match.index));
    parts.push(
      <a key={`${match.index}-${match[2]}`} href={match[2]} target="_blank" rel="noreferrer">
        {match[1]}
      </a>,
    );
    cursor = match.index + match[0].length;
  }
  if (cursor < value.length) parts.push(value.slice(cursor));
  return parts.length > 0 ? parts : value;
}

const ruleButtonStyle = (active) => ({
  minWidth: 38,
  height: 28,
  padding: "0 9px",
  border: active ? "1px solid #1f2937" : "1px solid var(--audit-border)",
  borderRadius: 4,
  background: active ? "#1f2937" : "var(--audit-bg-elevated)",
  color: active ? "#fff" : "var(--audit-text)",
  fontFamily: "inherit",
  fontSize: 12,
  fontWeight: active ? 700 : 500,
  cursor: "pointer",
});

// 관세사 신고 사례(trade_partner_history, 출처 익명) — hs4 단위.
// 인라인 필터 — 사용자가 특정 단어 (예: leather/bag) 입력 시 client-side
// line_item/hsk/규격·성분 매칭 필터.
function AgencyPrecedentsPanel({ hs4 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (!hs4) {
      setData(null);
      setLoading(false);
      return;
    }
    setLoading(true);
    setFilter("");
    fetch(`/api/onto/agency-precedents?hs4=${hs4}&limit=100`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))   // 네트워크 실패는 "자료 없음"으로(처리되지 않은 오류를 남기지 않는다)
      .finally(() => setLoading(false));
  }, [hs4]);

  if (loading) {
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        불러오는 중…
      </div>
    );
  }
  if (!data || !data.items || data.items.length === 0) {
    return (
      <div style={emptyStyle}>
        이 호의 관세사 사례 없음
      </div>
    );
  }

  const all = data.items;
  const filterLower = filter.trim().toLowerCase();
  const filtered = filterLower
    ? all.filter((it) => {
        const hay =
          `${it.hsk || ""} ${it.line_item || ""} ${it.spec || ""} ${it.composition || ""} ${it.standard_popum || ""}`.toLowerCase();
        return hay.includes(filterLower);
      })
    : all;

  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        <input
          type="text"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="거래품명 / HSK / 규격·성분 필터 (예: leather, bag, 4202 등)"
          style={{
            flex: 1,
            padding: "5px 8px",
            border: "1px solid var(--audit-border)",
            borderRadius: 4,
            background: "var(--audit-bg)",
            fontSize: 12,
            color: "var(--audit-text)",
            fontFamily: "inherit",
          }}
        />
        <span
          style={{
            fontSize: 12,
            color: "var(--audit-text-muted)",
            fontFamily: "JetBrains Mono, monospace",
            whiteSpace: "nowrap",
          }}
        >
          {filterLower ? `${filtered.length}/${all.length}` : `${all.length}건`}
        </span>
        {filterLower && (
          <button
            onClick={() => setFilter("")}
            style={{
              padding: "4px 8px",
              border: "1px solid var(--audit-border)",
              background: "var(--audit-bg)",
              color: "var(--audit-text-muted)",
              borderRadius: 4,
              cursor: "pointer",
              fontSize: 12,
              fontFamily: "inherit",
            }}
            title="필터 초기화"
          >
            ×
          </button>
        )}
      </div>
      <div
        style={{
          fontSize: 12,
          color: "var(--audit-text-faint)",
          marginBottom: 6,
        }}
      >
        신고이력의 <b>거래품명·규격·성분·표준품명</b> 분리 (일부 이력은 거래품명만 제공)
      </div>
      {filtered.length === 0 ? (
        <div style={emptyStyle}>필터 매칭 없음</div>
      ) : (
        <table
          style={{
            width: "100%",
            fontSize: 12,
            borderCollapse: "collapse",
            tableLayout: "fixed",
          }}
        >
          <colgroup>
            <col style={{ width: 100 }} />
            <col />
            <col />
            <col />
            <col />
            <col style={{ width: 80 }} />
            <col style={{ width: 50 }} />
          </colgroup>
          <thead>
            <tr style={{ background: "var(--audit-bg-subtle)" }}>
              <th style={thStyle(null)}>HSK</th>
              <th style={thStyle(null)}>거래품명</th>
              <th style={thStyle(null)}>규격</th>
              <th style={thStyle(null)}>성분</th>
              <th style={thStyle(null)}>표준품명</th>
              <th style={thStyle(null)}>최근</th>
              <th style={thStyle(null)}>건수</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it, i) => (
              <tr
                key={i}
                style={{ borderTop: "1px solid var(--audit-border)" }}
              >
                <td
                  style={{
                    padding: 5,
                    fontFamily: "JetBrains Mono, monospace",
                    color: "var(--audit-text)",
                  }}
                >
                  {highlightMatch(it.hsk, filterLower)}
                </td>
                <td style={ellipsisCellStyle} title={it.line_item}>
                  {highlightMatch(it.line_item, filterLower)}
                </td>
                <td
                  style={{
                    ...ellipsisCellStyle,
                    color: "var(--audit-text-muted)",
                  }}
                  title={it.spec || ""}
                >
                  {highlightMatch(it.spec || "—", filterLower)}
                </td>
                <td
                  style={{
                    ...ellipsisCellStyle,
                    color: "var(--audit-text-muted)",
                  }}
                  title={it.composition || ""}
                >
                  {highlightMatch(it.composition || "—", filterLower)}
                </td>
                <td
                  style={{
                    ...ellipsisCellStyle,
                    color: "var(--audit-text-muted)",
                  }}
                  title={it.standard_popum || ""}
                >
                  {highlightMatch(it.standard_popum || "—", filterLower)}
                </td>
                <td
                  style={{
                    padding: 5,
                    fontFamily: "JetBrains Mono, monospace",
                    color: "var(--audit-text-muted)",
                    fontSize: 12,
                  }}
                >
                  {(it.last_date || "").slice(0, 10)}
                </td>
                <td
                  style={{
                    padding: 5,
                    fontFamily: "JetBrains Mono, monospace",
                    color: "var(--audit-text-muted)",
                    textAlign: "right",
                  }}
                >
                  {it.cnt}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

const ellipsisCellStyle = {
  padding: 5,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

// 필터 단어 highlight — 매칭 부분 노란 배경.
function highlightMatch(text, filterLower) {
  if (!text || !filterLower) return text;
  const lower = String(text).toLowerCase();
  const idx = lower.indexOf(filterLower);
  if (idx < 0) return text;
  return (
    <>
      {text.slice(0, idx)}
      <mark
        style={{
          background: "#fff3a0",
          color: "inherit",
          padding: "0 1px",
        }}
      >
        {text.slice(idx, idx + filterLower.length)}
      </mark>
      {text.slice(idx + filterLower.length)}
    </>
  );
}

// 탭 라벨 → customs_hs_explanatory note_type (서버 필터)
const NOTE_TYPE_OF = { 부주: "sctManl", 류주: "hstdManl", 호해설: "hsfdManl" };

function NotesPanel({ hs4, chapter, section, lang, noteLabel, empty }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    // note_type 서버 필터 + full 전문 — 부 탭은 21개 부 모두 부 제목 원문을 표시하고,
    // 법정 부주가 있는 부는 같은 원문에 포함된 주 전체를 함께 표시한다.
    // 71류처럼 호가 10개 이상이면 limit 가 호해설로 차버려 류주가 아예 안 오고,
    // 600자 절단으로 주 규정 뒷부분이 잘리던 문제도 함께 막는다.
    const u = new URL("/api/onto/notes-search", window.location.origin);
    u.searchParams.set("q", "");
    u.searchParams.set("hs4", hs4 || "");
    u.searchParams.set("lang", lang);
    u.searchParams.set("limit", "5");
    if (NOTE_TYPE_OF[noteLabel]) u.searchParams.set("note_type", NOTE_TYPE_OF[noteLabel]);
    u.searchParams.set("full", "1");
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d) {
          setData(null);
          return;
        }
        // 호해설은 서버가 같은 류의 앞 호 해설까지 채워 보내므로(문의 #73) 이 호 것만 남긴다.
        const ownHeading = noteLabel === "호해설" && /^\d{4}$/.test(hs4 || "")
          ? new RegExp(`제\\s*${hs4}\\s*호`)
          : null;
        const filtered = (d.items || []).filter(
          (it) => it.label && it.label.includes(noteLabel)
            && (!ownHeading || ownHeading.test(it.label)),
        );
        setData({ items: filtered });
      })
      .catch(() => setData(null))   // 네트워크 실패는 "자료 없음"으로(처리되지 않은 오류를 남기지 않는다)
      .finally(() => setLoading(false));
  }, [hs4, chapter, section, lang, noteLabel]);

  if (loading) {
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        불러오는 중…
      </div>
    );
  }
  if (!data || !data.items || data.items.length === 0) {
    return <div style={emptyStyle}>{empty}</div>;
  }
  return (
    <div>
      {data.items.map((it, i) => (
        <div
          key={i}
          style={{
            padding: "8px 0",
            borderTop: i > 0 ? "1px solid var(--audit-border)" : "none",
            fontSize: 12,
            lineHeight: 1.65,
            whiteSpace: "pre-wrap",
          }}
        >
          <div
            style={{
              fontSize: 12,
              color: "var(--audit-text-muted)",
              marginBottom: 6,
              fontFamily: "JetBrains Mono, monospace",
            }}
          >
            {it.label}
          </div>
          <div>{(it.desc || "").replace(/<\/?[a-z][^>]*>/gi, "")}</div>
        </div>
      ))}
    </div>
  );
}

function PrecedentsPanel({ hs4, hsk }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedIdx, setExpandedIdx] = useState(null);

  useEffect(() => {
    setExpandedIdx(null);
    setLoading(true);
    const u = new URL("/api/onto/precedents-search", window.location.origin);
    u.searchParams.set("q", "");
    // 세번(hsk) 단계면 hsk 정확 매칭, 호 단계면 hs4 (호 전체).
    if (hsk && /^[0-9]{10}$/.test(hsk)) {
      u.searchParams.set("hsk", hsk);
    } else {
      u.searchParams.set("hs4", hs4 || "");
    }
    u.searchParams.set("mode", "keyword");
    u.searchParams.set("limit", hsk ? "200" : "20");
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setData(d))
      .catch(() => setData(null))   // 네트워크 실패는 "자료 없음"으로(처리되지 않은 오류를 남기지 않는다)
      .finally(() => setLoading(false));
  }, [hs4, hsk]);

  if (loading) {
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        불러오는 중…
      </div>
    );
  }
  if (!data || !data.items || data.items.length === 0) {
    return (
      <div style={emptyStyle}>
        {hsk ? "이 세번에 해당하는 분류사례 없음" : "이 호의 분류사례 없음"}
      </div>
    );
  }
  // 우측 EvidencePanel 과 동일한 rich 카드 (출처·참조번호·물품설명·결정사유) 재사용.
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {data.items.map((it, i) => (
        <PrecedentRow
          key={i}
          item={it}
          expanded={expandedIdx === i}
          onToggle={() => setExpandedIdx(expandedIdx === i ? null : i)}
        />
      ))}
    </div>
  );
}

// 이미지 탭 — 해당 호/세번 분류사례의 첨부 이미지 grid (image_atch_ids hot-link).
function ImagesPanel({ hs4, hsk, query = "", onResultsChange, onOpenCases }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    const u = new URL("/api/onto/precedents-search", window.location.origin);
    u.searchParams.set("q", String(query || "").trim());
    if (hsk && /^[0-9]{10}$/.test(hsk)) u.searchParams.set("hsk", hsk);
    else u.searchParams.set("hs4", hs4 || "");
    u.searchParams.set("mode", "keyword");
    u.searchParams.set("limit", hsk ? "200" : "50");
    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        setData(d);
        const items = Array.isArray(d?.items) ? d.items : [];
        const imageCases = items.filter(
          (it) => Array.isArray(it.image_atch_ids) && it.image_atch_ids.length,
        );
        onResultsChange?.({
          shown: imageCases.length,
          limited: items.length >= (hsk ? 200 : 50),
        });
      })
      .catch(() => setData(null))   // 네트워크 실패는 "자료 없음"으로(처리되지 않은 오류를 남기지 않는다)
      .finally(() => setLoading(false));
  }, [hs4, hsk, onResultsChange, query]);

  if (loading)
    return (
      <div style={{ color: "var(--audit-text-muted)", fontSize: 12 }}>
        불러오는 중…
      </div>
    );
  const withImg = (data?.items || []).filter(
    (it) => Array.isArray(it.image_atch_ids) && it.image_atch_ids.length,
  );
  if (withImg.length === 0)
    return (
      <div style={emptyStyle}>
        이미지가 있는 분류사례 없음 (관세청 CLIP 이미지 미보유 또는 미크롤)
      </div>
    );
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 10 }}>
      {withImg.flatMap((it) =>
        it.image_atch_ids.map((aid, k) => {
          const code = it.hsk || it.hs6 || "";
          return (
            <div
              key={`${it.case_id}-${k}`}
              title={`${code} ${it.label || ""}`}
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: 3,
              }}
            >
              <button
                type="button"
                data-case-id={it.case_id || undefined}
                onClick={() => onOpenCases?.(code, it.case_id)}
                aria-label={`${it.label || it.case_id} 분류사례 열기`}
                title={`${it.label || it.case_id} 분류사례 열기`}
                style={{
                  display: "block",
                  width: 130,
                  height: 130,
                  padding: 0,
                  border: 0,
                  background: "transparent",
                  cursor: "pointer",
                }}
              >
                <img
                  src={CLIP_IMG_BASE + encodeURIComponent(aid)}
                  alt={it.label || it.case_id}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    border: "1px solid var(--audit-border)",
                    borderRadius: 4,
                    background: "var(--audit-bg-subtle)",
                  }}
                />
              </button>
              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                <button
                  type="button"
                  onClick={() => onOpenCases?.(code, it.case_id)}
                  aria-label={`${code || "해당 세번"} 분류사례 보기`}
                  title={`${code || "해당 세번"} 품목분류사례 보기`}
                  style={{
                    border: 0,
                    padding: "1px 3px",
                    background: "transparent",
                    fontSize: 12,
                    color: "var(--audit-text)",
                    maxWidth: 98,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    fontFamily: "JetBrains Mono, monospace",
                    textDecoration: "underline",
                    textUnderlineOffset: 2,
                    cursor: "pointer",
                  }}
                >
                  {code}
                </button>
                <a
                  href={CLIP_IMG_BASE + encodeURIComponent(aid)}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`${it.label || it.case_id} 원본 이미지 보기`}
                  title="원본 이미지 새 창으로 열기"
                  style={{
                    color: "var(--audit-text-muted)",
                    fontSize: 11.5,
                    textDecoration: "none",
                  }}
                >
                  ↗
                </a>
              </div>
            </div>
          );
        }),
      )}
    </div>
  );
}

const thStyle = (width) => ({
  textAlign: "left",
  padding: 5,
  color: "var(--audit-text-muted)",
  fontSize: 12,
  fontWeight: 600,
  width: width || undefined,
});

const emptyStyle = {
  padding: 12,
  color: "var(--audit-text-muted)",
  fontSize: 12,
  fontStyle: "italic",
  border: "1px dashed var(--audit-border)",
  borderRadius: 4,
  background: "var(--audit-bg-subtle)",
};
